import React from 'react';
import '../../styles/ekyb.css';

const StepReport = ({ verificationData, onNewVerification }) => {
  const { entity, amlResult, micaResult, jurisdiction, verificationId } = verificationData;

  const getRiskBadgeClass = (risk) => {
    if (!risk) return 'low';
    const riskLower = risk.toLowerCase();
    if (riskLower === 'low') return 'low';
    if (riskLower === 'medium') return 'medium';
    if (riskLower === 'high' || riskLower === 'reject') return 'high';
    return 'low';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatTimestamp = () => {
    const now = new Date();
    return now.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const requiresManualReview = amlResult && (
    amlResult.overallRisk === 'MEDIUM' ||
    amlResult.overallRisk === 'HIGH' ||
    amlResult.overallRisk === 'REJECT'
  );

  return (
    <div className="ekyb-step-container">
      <div className="ekyb-step-header">
        <h2 className="ekyb-step-title">Verification Report</h2>
        <p className="ekyb-step-subtitle">
          Complete eKYB verification report for {entity.name}
        </p>
      </div>

      <div className="ekyb-report-summary">
        <div className="ekyb-report-summary-title">Overall Risk Rating</div>
        <span className={`ekyb-report-summary-badge ekyb-status-badge ${getRiskBadgeClass(amlResult?.overallRisk)}`}>
          {amlResult?.overallRisk || 'LOW RISK'}
        </span>
      </div>

      {requiresManualReview && (
        <div className="ekyb-alert warning">
          <strong>Manual Compliance Review Required</strong><br />
          This verification has been flagged for manual review due to {amlResult.overallRisk} risk rating. Contact compliance team before proceeding.
        </div>
      )}

      <div className="ekyb-report-section">
        <h3 className="ekyb-report-section-title">Verification Summary</h3>
        <table className="ekyb-data-table">
          <tbody>
            <tr>
              <td>Verification ID</td>
              <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{verificationId || `EKYB-${Date.now()}`}</td>
            </tr>
            <tr>
              <td>Verification Timestamp</td>
              <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimestamp()}</td>
            </tr>
            <tr>
              <td>Jurisdiction</td>
              <td style={{ textTransform: 'capitalize' }}>{jurisdiction}</td>
            </tr>
            <tr>
              <td>Entity Status</td>
              <td>
                <span className="ekyb-status-badge active">{entity.status || 'VERIFIED'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="ekyb-report-section">
        <h3 className="ekyb-report-section-title">Entity Information</h3>
        <table className="ekyb-data-table">
          <tbody>
            <tr>
              <td>Legal Name</td>
              <td>{entity.name}</td>
            </tr>
            <tr>
              <td>Registration Number</td>
              <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{entity.registrationNumber || entity.companyId}</td>
            </tr>
            <tr>
              <td>Registration Date</td>
              <td>{formatDate(entity.registrationDate)}</td>
            </tr>
            <tr>
              <td>Legal Form</td>
              <td>{entity.legalForm || 'N/A'}</td>
            </tr>
            <tr>
              <td>Address</td>
              <td>{entity.address || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="ekyb-report-section">
        <h3 className="ekyb-report-section-title">AML Screening Results</h3>
        <table className="ekyb-data-table">
          <tbody>
            <tr>
              <td>Overall Risk Rating</td>
              <td>
                <span className={`ekyb-status-badge ${getRiskBadgeClass(amlResult?.overallRisk)}`}>
                  {amlResult?.overallRisk || 'LOW'}
                </span>
              </td>
            </tr>
            <tr>
              <td>UBOs Screened</td>
              <td>{amlResult?.uboScreening?.length || 0}</td>
            </tr>
            <tr>
              <td>Screening Provider</td>
              <td>OpenSanctions</td>
            </tr>
            <tr>
              <td>Screening Coverage</td>
              <td>Sanctions, PEP, Adverse Media</td>
            </tr>
          </tbody>
        </table>
      </div>

      {micaResult && (
        <div className="ekyb-report-section">
          <h3 className="ekyb-report-section-title">MiCA Register Status</h3>
          <table className="ekyb-data-table">
            <tbody>
              <tr>
                <td>CASP Registration</td>
                <td>
                  <span className={`ekyb-status-badge ${micaResult.registered ? 'active' : 'inactive'}`}>
                    {micaResult.registered ? 'REGISTERED' : 'NOT REGISTERED'}
                  </span>
                </td>
              </tr>
              {micaResult.registered && micaResult.caspId && (
                <tr>
                  <td>CASP ID</td>
                  <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{micaResult.caspId}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="ekyb-button-group">
        <button
          className="ekyb-button ekyb-button-secondary"
          onClick={() => window.print()}
        >
          Print Report
        </button>
        <button
          className="ekyb-button ekyb-button-primary"
          onClick={onNewVerification}
        >
          New Verification
        </button>
      </div>
    </div>
  );
};

export default StepReport;
